import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FONT_SIZE_OPTIONS } from '../../../Utils/fontSize';
import { useFontSize } from '../../../Contexts/FontSizeContext';
import styles from './LanguageSelector.module.css';

const PREVIEW_SIZE = {
  small: '0.75rem',
  medium: '0.875rem',
  large: '1rem',
};

export function FontSizeSelector() {
  const { t } = useTranslation();
  const { fontSize, setFontSize } = useFontSize();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const selected = FONT_SIZE_OPTIONS.find((option) => option.value === fontSize)
    || FONT_SIZE_OPTIONS[1];

  const changeSize = (value) => {
    setFontSize(value);
    setMenuOpen(false);
  };

  return (
    <div className={`${styles.languageSelector} ${styles.fontSizeSelector}`} ref={rootRef}>
      <button
        type="button"
        className={classNames(styles.trigger, { [styles.triggerOpen]: menuOpen })}
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-label={t('FontSize')}
      >
        <span className={styles.aaMark} aria-hidden="true">Aa</span>
        <span className={styles.label}>
          {t(selected.labelKey)}
        </span>
        <svg
          className={classNames(styles.chevron, { [styles.chevronOpen]: menuOpen })}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {menuOpen && (
        <div className={styles.menu} role="listbox" aria-label={t('FontSize')}>
          {FONT_SIZE_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={fontSize === option.value}
              onClick={() => changeSize(option.value)}
              className={classNames(styles.menuItem, {
                [styles.selectedItem]: fontSize === option.value,
              })}
              style={{ fontSize: PREVIEW_SIZE[option.value] }}
            >
              <span className={styles.langName}>{t(option.labelKey)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
